import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSimilarLive } from '../lib/catalog-client';
import { track } from '../lib/funnel';
import { useT } from '../i18n/locale-context';
import { TenderList } from './tender-list';
import { SaveSearchButton } from './save-search-button';

/**
 * «Приём заявок завершён {дата}. Итоги пока не опубликованы» — the honest
 * status of a lot whose bidding is over and whose outcome nobody has published
 * yet (`isClosedWithoutOutcome`).
 *
 * Why it exists (ChatGPT audit 2026-10-08, item 1): 53% of the people ChatGPT
 * sends to a tender page land on one that is already over, and they pay five
 * times less often (0.6% vs 3.0%) than those landing on a live lot. Before
 * this the page simply had nothing to say about it — the status pill in the
 * header and nothing else, and no way forward but the back button.
 *
 * No padlock and no promises on purpose: this is a statement of fact, not a
 * wall, and we do not know whether or when the outcome will appear.
 */
export function ClosedLotNotice({ deadline }: { deadline: string | null }) {
  const t = useT();
  return (
    <div
      role="status"
      data-testid="closed-lot-notice"
      className="rounded-lg border border-edge bg-bone-surface p-4 shadow-m-sm sm:p-5"
    >
      <p className="text-[14px] font-semibold text-ink">
        {deadline
          ? t('tender.closedNoOutcome.title', { date: deadline })
          : t('tender.closedNoOutcome.titleNoDate')}
      </p>
      <p className="mt-0.5 text-[13px] text-ink-muted">{t('tender.closedNoOutcome.body')}</p>
    </div>
  );
}

/** Below this the block does not render — two cards read as noise. Mirrors
 *  `SIMILAR_LIVE_MIN` in the API, which never sends fewer than this on
 *  purpose, but the near-duplicate-title collapse can. */
export const SIMILAR_LIVE_MIN = 3;

/** One query definition for the block and for the page that has to know
 *  whether the block is on screen — React Query dedups the two by key. */
function similarLiveQuery(tenderId: string, locale: string) {
  return {
    queryKey: ['catalog', 'similar-live', tenderId, locale],
    queryFn: () => fetchSimilarLive(tenderId, locale),
    staleTime: 5 * 60 * 1000,
    // A miss here costs nothing worth retrying for: the block is optional.
    retry: false,
  } as const;
}

/**
 * Is «Похожие открытые лоты» on screen? `true` — yes; `false` — settled and
 * it is not (fewer than three, or the request failed); `null` — not asked
 * (`enabled` false) or still loading.
 *
 * The page reads it to drop the old embedding-based «Похожие тендеры» at the
 * bottom (Igor, 09.10): two «similar» lists on one finished lot is one too
 * many. While the answer is pending the caller should treat it as "shown", so
 * the old block does not flash in and out; when the new block settles to
 * nothing, the old one comes back — it is then the only way forward on the page.
 */
export function useSimilarLiveShown(
  tenderId: string,
  locale: string,
  enabled: boolean,
): boolean | null {
  const { data, status } = useQuery({ ...similarLiveQuery(tenderId, locale), enabled });
  if (!enabled) return null;
  if (status === 'error') return false;
  if (status !== 'success') return null;
  return (data?.items.length ?? 0) >= SIMILAR_LIVE_MIN;
}

/**
 * «Похожие открытые лоты» under the notice: 3–5 lots still open for bids,
 * from the same ЕНКТ code widened step by step (exact → class → group →
 * section), else the same category — and «Следить за такими», which is the
 * EXISTING «Сохранить поиск» flow (`SaveSearchButton`: guest → stash →
 * register → created on return; signed-in → the usual popover with the free
 * cap) pre-filled with whichever rung filled the block. No new mechanism.
 *
 * Client-rendered only. The lots that show it are not prerendered (finished,
 * no winner → `noindex`, SPA fallback), and the ones that ARE in an older
 * release hydrate, refetch the lot, and only then learn it is over — the
 * prerendered HTML is untouched either way.
 *
 * Renders nothing while loading, on error, or with fewer than three lots: a
 * heading over one card, or a spinner pushing the description down, is worse
 * than no block.
 */
export function SimilarLiveLots({
  tenderId,
  locale,
  scope,
  followName,
}: {
  tenderId: string;
  locale: string;
  /** The closed lot's layer — the subscription must stay in it (an `ifi`
   *  saved search with no scope would silently become `gov`). */
  scope?: string | null;
  /** Name suggestion for the saved search when the follow target is a bare
   *  code (the popover can name a category, not an ИКПУ prefix). */
  followName?: string;
}) {
  const t = useT();
  const { data } = useQuery(similarLiveQuery(tenderId, locale));

  const items = data?.items ?? [];
  const visible = items.length >= SIMILAR_LIVE_MIN;
  const level = data?.level ?? null;

  // Once per lot per mount — the denominator for `similar_live_clicked`.
  const shownFor = useRef<string | null>(null);
  const followClickedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!visible || shownFor.current === tenderId) return;
    shownFor.current = tenderId;
    track('similar_live_shown', { id: tenderId, level, n: items.length });
  }, [visible, tenderId, level, items.length]);

  if (!visible || !data) return null;

  const follow = data.follow;
  const followScope = scope && scope !== 'gov' ? scope : undefined;

  return (
    <section
      aria-labelledby="similar-live-heading"
      data-testid="similar-live"
      className="flex flex-col gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="similar-live-heading" className="text-[15px] font-semibold text-ink">
          {t('tender.similarLive.title')}
        </h2>
        {follow && (
          <div
            // The popover renders inside this wrapper too, so its own clicks
            // (name field, Save, Cancel) bubble through here — count the
            // follow intent once per lot, not once per click.
            onClickCapture={() => {
              if (followClickedFor.current === tenderId) return;
              followClickedFor.current = tenderId;
              track('similar_live_clicked', { id: tenderId, target: 'follow', level });
            }}
          >
            <SaveSearchButton
              {...('ikpu' in follow ? { ikpu: follow.ikpu } : { category: follow.category })}
              scope={followScope}
              label={t('tender.similarLive.follow')}
              defaultName={followName}
            />
          </div>
        )}
      </div>
      {/* Click delegation instead of a prop on TenderList: the list is shared
          by the catalogue and «Мои тендеры», and a lot click is the only thing
          worth knowing here. Capture phase, so it runs before navigation. */}
      <div
        onClickCapture={(e) => {
          const a = (e.target as HTMLElement).closest('a[href]');
          if (!a) return;
          const href = a.getAttribute('href') ?? '';
          const position = items.findIndex((it) => href.includes(it.id));
          if (position < 0) return;
          track('similar_live_clicked', {
            id: tenderId,
            target: 'lot',
            to: items[position].id,
            position: position + 1,
            level,
          });
        }}
      >
        <TenderList items={items} />
      </div>
    </section>
  );
}
